import { Component, Vue, Watch } from "vue-property-decorator";
import { ActionItem, fmtMsg } from "commonui";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { Select, Button, Alert, Form } from "ant-design-vue";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { LanguageSkillModel, VCSLevelModel } from "@/models/title-list-model";
import {
	fetchLevels,
	getEndLevelBasedOnStart,
	getGridLevels,
	getRouteAccessLevel,
	getSkillSelected
} from "@/util/func";
import { VersionGroupModule } from "@/modules/versiongroup";
import { isAnyVersionSelected } from "@/util/func";
import {
	WordCountReportRequestModel,
	MaterialTitleRequestModel,
	MaterialTitleResponseModel,
	WordCoundReportResponseModel,
	ReportManagerService
} from "@/services/text-writer/word-count";
import { CommonLocale, WordCountLocale } from "@/locales/localeid";
import { IWorkbookCell } from "wijmo/wijmo.xlsx";
import { AllLanguageSkill } from "@/util/constants";
import PageTitle from "@/components/page-title/page-title.vue";
import { AccessLevel, CurriculumType } from "@/util/enum";

@Component({
	components: {
		Grid,
		Select,
		Option: Select.Option,
		Button,
		ActionItem,
		Alert,
		Form,
		FormItem: Form.Item,
		PageTitle
	}
})
export default class TextWriterWordCount extends Vue {
	public loading: boolean = false;
	public titleListModule = getModule(TitleListState);
	public startLevel: VCSLevelModel[] = [];
	public endLevel: VCSLevelModel[] = [];
	public startLevelSelected: number = 0;
	public endLevelSelected: number = 0;
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	public materialTitleSelected: string | null = "";
	public wordCountService = ReportManagerService();
	public materialTitleData: MaterialTitleResponseModel[] = [];
	public materialTitleLoading: boolean = false;
	public wordCountReport: WordCoundReportResponseModel[] = [];
	public format = fmtMsg;
	public locale = WordCountLocale;
	public commonLocale = CommonLocale;
	public noVersion: boolean = false;
	public gridStartLevel: number = 0;
	public gridEndLevel: number = 0;
	public skillSelected: number[] = [];
	public skillDisabled: boolean = false;
	public units: VCSLevelModel[] = [];
	public readonly allLanguageSkill = AllLanguageSkill;
	public form;
	public materialLabelCol = { span: 5 };
	public accessLevel = AccessLevel;

	get languageSkills(): LanguageSkillModel[] {
		return this.titleListModule.languageSkills;
	}
	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	created() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}

		this.form = this.$form.createForm(this, { name: "form" });
		this.units = fetchLevels(this.titleListModule.levels, this.versionGroupModule.selectedLsVersion, this.versionGroupModule.selectedGsVersion)
		this.skillSelected = [this.allLanguageSkill];
		this.selectStartAndEndLevel();
	}

	mounted() {
		this.versionGroupModule.setDisabled(false);
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	fetchMaterialTitle() {
		const all = {
			contentId: "All",
			materialTitle: this.format(this.locale.WordCountLanguageSkillSelectAll)
		};

		if (this.startLevelSelected !== this.endLevelSelected) {
			this.materialTitleData = [all];
			this.materialTitleSelected = this.materialTitleData[0].contentId;
			this.checkSkillDisabled();
			return Promise.resolve();
		}

		this.materialTitleLoading = true;

		const startLevel = this.startLevel.find((level) => level.id == this.startLevelSelected);
		const endLevel = this.endLevel.find((level) => level.id == this.endLevelSelected);
		const materialTitleRequestModel: MaterialTitleRequestModel = {
			startLevel: startLevel?.unit || 0,
			endLevel: endLevel?.unit || 0,
			startLevelCurriculumType: startLevel?.curriculumType as CurriculumType,
			endLevelCurriculumType: endLevel?.curriculumType as CurriculumType,
			selectedGSVersionGroupId:
				VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
			selectedLSVersionGroupId:
				VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null
		};

		return this.wordCountService.getMaterialTitle(materialTitleRequestModel).then((materialTitleData) => {
			if (materialTitleData.length) {
				this.materialTitleData = [
					all,
					...materialTitleData.sort((a, b) => (a.materialTitle < b.materialTitle ? -1 : 1))
				];
				this.materialTitleSelected = this.materialTitleData[0].contentId;
			} else {
				this.materialTitleData = [];
				this.materialTitleSelected = "No Data";
			}

			this.materialTitleLoading = false;
			this.checkSkillDisabled();
			return Promise.resolve();
		});
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	isVersionUpdated() {
		if (isAnyVersionSelected()) {
			this.noVersion = false;
			this.units = fetchLevels(this.titleListModule.levels, this.versionGroupModule.selectedLsVersion, this.versionGroupModule.selectedGsVersion)
			this.selectStartAndEndLevel();
			this.fetchMaterialTitle();
		} else {
			this.noVersion = true;
		}
	}

	setEndLevels(startLevelId: number) {
		this.endLevel = getEndLevelBasedOnStart(this.units, startLevelId);

		if (this.endLevel.length > 0) {
			this.endLevelSelected = this.endLevel[0].id;
		} else {
			this.endLevel = [];
			this.endLevelSelected = 0;
		}
		this.fetchMaterialTitle();
	}

	onSelectedStartLevelChange(value: number) {
		this.setEndLevels(value);
		this.checkSkillDisabled();
	}

	onSelectedEndLevelChange() {
		this.fetchMaterialTitle();
		this.checkSkillDisabled();
	}

	onSelectedMaterialTitleChange(id) {
		if (id === "All") {
			this.skillDisabled = false;
		} else {
			this.skillDisabled = true;
		}
	}

	onSkillChange(selectedValues) {
		this.skillSelected = getSkillSelected(selectedValues);
	}

	checkSkillDisabled() {
		if (this.materialTitleSelected === "All") {
			this.skillDisabled = false;
		} else {
			this.skillDisabled = true;
		}
	}

	showWordCountReport() {
		this.loading = true;
		const startLevel = this.startLevel.find((level) => level.id == this.startLevelSelected);
		const endLevel = this.endLevel.find((level) => level.id == this.endLevelSelected);

		const params: WordCountReportRequestModel = {
			startLevel: startLevel?.unit || 0,
			endLevel: endLevel?.unit || 0,
			startLevelCurriculumType: startLevel?.curriculumType as CurriculumType,
			endLevelCurriculumType: endLevel?.curriculumType as CurriculumType,
			contentId: this.materialTitleSelected === "All" ? null : this.materialTitleSelected,
			selectedGSVersionGroupId: VersionGroupModule.selectedGsVersion,
			selectedLSVersionGroupId: VersionGroupModule.selectedLsVersion,
			languageSkill: this.skillDisabled ? null : this.skillSelected
		};

		if (
			(VersionGroupModule.selectedLsVersion == "-1" && params.endLevelCurriculumType == 1) ||
			(VersionGroupModule.selectedGsVersion == "-1" && params.startLevelCurriculumType == 0)
		) {
			this.loading = false;
			this.wordCountReport = [];
			this.gridStartLevel = this.startLevelSelected;
			this.gridEndLevel = this.endLevelSelected;
			return;
		}
		return this.wordCountService
			.getWordCountReport(params)
			.then((data) => {
				this.wordCountReport = data;
				this.gridStartLevel = this.startLevelSelected;
				this.gridEndLevel = this.endLevelSelected;
				this.loading = false;
				return Promise.resolve();
			})
			.catch(() => {
				this.loading = false;
			});
	}

	get levels() {
		return getGridLevels(this.titleListModule.levels, this.gridStartLevel, this.gridEndLevel);
	}

	get exportOptions() {
		return {
			formatCell: (cell: IWorkbookCell, binding: string) => {
				if (binding.includes("Set") || binding.includes("Unit")) {
					cell.value = cell.value === null ? "0" : cell.value;
				}
			},
			filename: "Word Count Report"
		};
	}

	getCellValue(value, displayName) {
		if (value.item[displayName] == null) {
			return 0;
		} else {
			return value.item[displayName];
		}
	}

	filterOption(input, option) {
		return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
	}
	
	selectStartAndEndLevel() {
		this.startLevel = this.units;
		if (this.startLevel.length > 0) {
			this.startLevelSelected = this.startLevel[0].id;
			this.setEndLevels(this.startLevelSelected);
		}
	}
}
